<template>
  <header class="sf-nav">
    <div class="sf-nav-inner">
      <NuxtLink class="sf-logo" to="/" aria-label="SharkFoto home">
        Shark<b>Foto</b>
      </NuxtLink>

      <nav class="sf-menu" aria-label="Primary navigation">
        <div
          v-for="group in primaryToolGroups"
          :key="group.label"
          class="sf-menu-item"
        >
          <button type="button" class="sf-menu-link">
            <UIcon :name="group.icon" class="sf-mi-ico" />
            <span>{{ group.label }}</span>
            <UIcon name="i-lucide-chevron-down" class="sf-chev" />
          </button>
          <div class="sf-mega" :style="{ '--accent': group.accent }">
            <div class="sf-mega-head">
              {{ group.label }} · {{ group.children.length }} tools
            </div>
            <div
              class="sf-mega-grid"
              :class="group.children.length > 6 ? 'sf-mega-grid-2' : ''"
            >
              <NuxtLink
                v-for="child in group.children"
                :key="child.to"
                class="sf-mega-link"
                :to="child.to"
                :target="child.target"
              >
                <span class="sf-dot"></span>
                <span>{{ child.label }}</span>
              </NuxtLink>
            </div>
          </div>
        </div>

        <div class="sf-menu-item">
          <button type="button" class="sf-menu-link">
            <UIcon name="i-lucide-sparkles" class="sf-mi-ico" />
            <span>AI Effects</span>
            <UIcon name="i-lucide-chevron-down" class="sf-chev" />
          </button>
          <div
            class="sf-mega sf-effects-mega"
            style="--accent: var(--sf-brand)"
          >
            <div class="sf-mega-grid sf-mega-grid-2">
              <div>
                <div class="sf-mega-head sf-video-text">Video Effects</div>
                <NuxtLink
                  v-for="effect in navVideoEffects"
                  :key="effect.code"
                  class="sf-mega-link"
                  :to="effect.to"
                >
                  <span class="sf-dot sf-video-dot"></span>
                  <span>{{ effect.title }}</span>
                </NuxtLink>
                <NuxtLink
                  class="sf-mega-link sf-view-all sf-video-text"
                  to="/ai/effects/?type=video"
                >
                  <span class="sf-dot sf-video-dot"></span>
                  <span>View all video effects →</span>
                </NuxtLink>
              </div>
              <div>
                <div class="sf-mega-head sf-image-text">Image Effects</div>
                <NuxtLink
                  v-for="effect in navImageEffects"
                  :key="effect.code"
                  class="sf-mega-link"
                  :to="effect.to"
                >
                  <span class="sf-dot sf-image-dot"></span>
                  <span>{{ effect.title }}</span>
                </NuxtLink>
                <NuxtLink
                  class="sf-mega-link sf-view-all sf-image-text"
                  to="/ai/effects/?type=image"
                >
                  <span class="sf-dot sf-image-dot"></span>
                  <span>View all image effects →</span>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>

        <div class="sf-menu-item">
          <button type="button" class="sf-menu-link">
            <UIcon name="i-lucide-box" class="sf-mi-ico" />
            <span>AI Models</span>
            <UIcon name="i-lucide-chevron-down" class="sf-chev" />
          </button>
          <div
            class="sf-mega sf-models-mega"
            style="--accent: var(--sf-models)"
          >
            <div class="sf-mega-head">
              Frontier models · {{ aiModelItems.length }} available
            </div>
            <div class="sf-mm-grid">
              <NuxtLink
                v-for="model in navModels"
                :key="model.to"
                class="sf-mm-link"
                :to="model.to"
              >
                <span class="sf-mm-ico">
                  <img
                    v-if="model.avatar"
                    :src="model.avatar"
                    :alt="model.label"
                    loading="lazy"
                  />
                  <span v-else>{{ model.label.charAt(0) }}</span>
                </span>
                <span class="sf-mm-txt">
                  <span class="sf-mm-name">{{ model.label }}</span>
                  <span class="sf-mm-desc">
                    {{ model.vendor || model.description || "AI model" }}
                  </span>
                </span>
              </NuxtLink>
            </div>
            <NuxtLink class="sf-mm-foot" to="/ai/models/">
              View all {{ aiModelItems.length }} models
              <UIcon name="i-lucide-arrow-right" class="size-4" />
            </NuxtLink>
          </div>
        </div>

        <div class="sf-menu-item">
          <button type="button" class="sf-menu-link">
            <UIcon name="i-lucide-layout-grid" class="sf-mi-ico" />
            <span>Free Tools</span>
            <UIcon name="i-lucide-chevron-down" class="sf-chev" />
          </button>
          <div class="sf-mega sf-free-mega" style="--accent: var(--sf-free)">
            <div class="sf-mega-head">Free Tools · no sign-up</div>
            <div class="sf-free-cols">
              <div
                v-for="cat in freeToolCategories"
                :key="cat.to"
                class="sf-free-col"
                :style="{ '--accent': cat.accent }"
              >
                <div class="sf-free-col-head">{{ cat.label }}</div>
                <NuxtLink
                  v-for="tool in cat.tools"
                  :key="tool.to"
                  class="sf-mega-link"
                  :to="tool.to"
                >
                  <span class="sf-dot"></span>
                  <span>{{ tool.label }}</span>
                </NuxtLink>
                <NuxtLink class="sf-mega-link sf-free-all" :to="cat.to">
                  <span>{{ cat.allLabel }} →</span>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>

        <NuxtLink class="sf-menu-link" to="/pricing/">Pricing</NuxtLink>
        <!-- <a class="sf-menu-link" href="https://sharkfoto.com/blog/" target="_blank" rel="noopener noreferrer">
          Blog
        </a> -->
      </nav>

      <div class="sf-nav-cta">
        <template v-if="!token">
          <button
            type="button"
            class="sf-btn sf-btn-primary sf-desktop-auth"
            @click="openSignInModal"
          >
            Sign In
          </button>
          <button
            type="button"
            class="sf-btn sf-btn-outline sf-desktop-auth"
            @click="openSignUpModal"
          >
            Sign Up Free
          </button>
        </template>

        <template v-else>
          <UPopover
            :content="{ align: 'end', side: 'bottom', sideOffset: 8 }"
            :mode="creditsPopoverMode"
          >
            <button type="button" class="sf-credits-btn">
              <UIcon
                name="material-symbols-light:diamond-shine-outline-rounded"
                class="size-5"
              />
              <span>{{ fmtCredits(currentCredits) }}</span>
            </button>
            <template #content>
              <UCard
                class="bg-[#10131b] ring-1 ring-white/10 w-90"
                :ui="{ body: '!p-4' }"
              >
                <div class="flex items-center justify-between mb-3">
                  <div class="text-white font-semibold">{{ userPlanName }}</div>
                  <UButton
                    size="xs"
                    color="primary"
                    class="text-white"
                    @click="openBuyCreditsModal()"
                  >
                    Add Credits
                  </UButton>
                </div>
                <div class="h-px bg-white/10 my-3"></div>
                <div class="text-sm space-y-1.5">
                  <div
                    class="flex items-center justify-between gap-4 text-white"
                  >
                    <span>Credits</span>
                    <span class="tabular-nums">{{
                      fmtCredits(currentCredits)
                    }}</span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-4 text-white/55 text-[13px]"
                  >
                    <span>Free Credits</span>
                    <span class="tabular-nums">{{
                      fmtCredits(freeCredits)
                    }}</span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-4 text-white/55 text-[13px]"
                  >
                    <span>Monthly Credits</span>
                    <span class="tabular-nums">{{
                      fmtCredits(subscriptionCredits)
                    }}</span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-4 text-white/55 text-[13px]"
                  >
                    <span>Pay-As-You-Go Credits</span>
                    <span class="tabular-nums">{{
                      isFreePlan ? "-" : fmtCredits(paygCredits)
                    }}</span>
                  </div>
                </div>
                <div class="mt-3 text-sm text-white/80">
                  <NuxtLink
                    to="/my/plan-credits/"
                    class="hover:text-white transition-colors"
                  >
                    Manage My Plan &amp; Credits &gt;
                  </NuxtLink>
                </div>
              </UCard>
            </template>
          </UPopover>

          <UPopover
            v-model:open="userMenuOpen"
            :mode="userMenuMode"
            :content="{ align: 'end', side: 'bottom', sideOffset: 8 }"
            :ui="{
              content:
                'w-64 bg-[#10131b] shadow-lg rounded-md ring-1 ring-white/10 overflow-hidden',
            }"
            :open-delay="80"
            :close-delay="120"
          >
            <button type="button" class="sf-avatar-btn">
              <UAvatar
                :alt="username"
                size="md"
                :src="avatarUrl"
                :ui="{ fallback: 'text-xs' }"
              />
            </button>

            <template #content="{ close }">
              <div class="divide-y divide-white/10">
                <div
                  v-for="(group, groupIndex) in userMenuItems"
                  :key="`user-menu-group-${groupIndex}`"
                  class="p-1"
                >
                  <template
                    v-for="(item, itemIndex) in group"
                    :key="`user-menu-item-${groupIndex}-${itemIndex}`"
                  >
                    <div
                      v-if="item.type === 'label'"
                      class="flex items-center gap-2 px-2 py-2 text-sm"
                    >
                      <UAvatar
                        v-if="item.avatar"
                        v-bind="item.avatar"
                        size="md"
                        class="shrink-0"
                        :ui="{ fallback: 'text-xs' }"
                      />
                      <div class="min-w-0">
                        <div class="truncate text-white/90 font-medium">
                          {{ fullName || userEmail || item.label }}
                        </div>
                        <div
                          v-if="fullName && userEmail"
                          class="truncate text-xs text-white/60"
                        >
                          {{ userEmail }}
                        </div>
                      </div>
                    </div>

                    <button
                      v-else-if="item.type === 'checkbox'"
                      type="button"
                      class="sf-user-menu-row justify-between"
                      @click="onUserMenuItemSelect(item, close)"
                    >
                      <span class="flex items-center gap-2 min-w-0">
                        <UIcon
                          v-if="item.icon"
                          :name="item.icon"
                          class="size-5 shrink-0"
                        />
                        <span class="truncate">{{ item.label }}</span>
                      </span>
                      <UCheckbox
                        :model-value="Boolean(item.checked)"
                        size="xs"
                        @update:model-value="
                          item.onUpdateChecked?.(Boolean($event))
                        "
                        @click.stop
                      />
                    </button>

                    <NuxtLink
                      v-else-if="item.to && !isExternalUrl(item.to)"
                      :to="item.to"
                      class="sf-user-menu-row"
                      :class="[
                        item.active ? 'bg-white/10 text-white' : '',
                        item.class,
                      ]"
                      @click="closeUserMenu(close)"
                    >
                      <UIcon
                        v-if="item.icon"
                        :name="item.icon"
                        class="size-5 shrink-0"
                      />
                      <span class="truncate">{{ item.label }}</span>
                    </NuxtLink>

                    <a
                      v-else-if="item.to && isExternalUrl(item.to)"
                      :href="item.to"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="sf-user-menu-row"
                      :class="item.class"
                      @click="closeUserMenu(close)"
                    >
                      <UIcon
                        v-if="item.icon"
                        :name="item.icon"
                        class="size-5 shrink-0"
                      />
                      <span class="truncate">{{ item.label }}</span>
                    </a>

                    <button
                      v-else
                      type="button"
                      class="sf-user-menu-row"
                      :class="
                        item.color === 'error'
                          ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                          : item.class
                      "
                      @click="onUserMenuItemSelect(item, close)"
                    >
                      <UIcon
                        v-if="item.icon"
                        :name="item.icon"
                        class="size-5 shrink-0"
                        :class="
                          item.icon === 'eos-icons:loading'
                            ? 'animate-spin'
                            : ''
                        "
                      />
                      <span class="truncate">{{ item.label }}</span>
                    </button>
                  </template>
                </div>
              </div>
            </template>
          </UPopover>
        </template>

        <button
          type="button"
          class="sf-mobtoggle"
          :aria-expanded="mobileMenuOpen"
          aria-label="menu"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <UIcon name="i-lucide-menu" class="size-5" />
        </button>
      </div>
    </div>

    <div class="sf-mobile-menu" :class="{ open: mobileMenuOpen }">
      <div
        v-for="section in mobileSections"
        :key="section.label"
        class="sf-mob-acc"
        :class="{ open: activeMobileSection === section.label }"
      >
        <button
          type="button"
          class="sf-mob-head"
          @click="toggleMobileSection(section.label)"
        >
          <span class="sf-mob-title">
            <span
              class="sf-mob-dot"
              :style="{ background: section.accent }"
            ></span>
            {{ section.label }}
          </span>
          <UIcon name="i-lucide-chevron-down" class="sf-mob-chev" />
        </button>
        <div class="sf-mob-sub">
          <div class="sf-mob-sub-in">
            <div class="sf-mob-sub-pad">
              <template
                v-for="entry in section.children"
                :key="entry.key || entry.to || entry.label"
              >
                <div v-if="entry.type === 'label'" class="sf-mob-sublabel">
                  {{ entry.label }}
                </div>
                <NuxtLink
                  v-else
                  class="sf-mob-link-child"
                  :to="entry.to"
                  @click="closeMobileMenu"
                >
                  {{ entry.label }}
                </NuxtLink>
              </template>
            </div>
          </div>
        </div>
      </div>

      <NuxtLink class="sf-mob-link" to="/pricing/" @click="closeMobileMenu"
        >Pricing</NuxtLink
      >
      <a
        class="sf-mob-link"
        href="https://sharkfoto.com/blog/"
        target="_blank"
        rel="noopener noreferrer"
        @click="closeMobileMenu"
      >
        Blog
      </a>

      <div v-if="!token" class="sf-mob-cta">
        <button
          type="button"
          class="sf-btn sf-btn-primary"
          @click="openSignInFromMobile"
        >
          Sign In
        </button>
        <button
          type="button"
          class="sf-btn sf-btn-outline"
          @click="openSignUpFromMobile"
        >
          Sign Up Free
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { useStore } from "vuex";
import { normalizeEffectsList } from "@/composables/useAiR2ModelsAndEffects";
import {
  enabledModels,
  findMenuGroup,
  menuGroupsForArea,
  productForMenuItem,
  toInternalRoute,
  createProductIndexes,
} from "@/utils/aiCatalogData";

const props = defineProps({
  isIndex: {
    type: Boolean,
    default: false,
  },
  redirectAfterLogin: {
    type: String,
    default: null,
  },
});

const store = useStore();
const route = useRoute();
const menuData = inject("menuData", ref([]));
const productList = inject("productList", ref([]));
const modelList = inject("modelList", ref([]));
const r2CachedFetch = useR2CachedFetch();

const { openSignInModal: _openSignInModal, openSignUpModal: _openSignUpModal } =
  useAuthModals();
const { openBuyCreditsModal } = useCreditsModal();

const openSignInModal = () => _openSignInModal(props.redirectAfterLogin);
const openSignUpModal = () => _openSignUpModal(props.redirectAfterLogin);

const token = computed(() => store.state.user.token);
const userInfo = computed(() => store.state.user.userInfo);
const currentCredits = computed(() => userInfo.value?.credits_sum || 0);
const freeCredits = computed(() => Number(userInfo.value?.credits || 0));
const subscriptionCredits = computed(() =>
  Number(userInfo.value?.subscription_credits || 0)
);
const paygCredits = computed(() =>
  Number(userInfo.value?.additional_credits || 0)
);
const avatarUrl = computed(() => store.state.user.avatarUrl);
const isFreePlan = computed(() => Number(userInfo.value?.product_id) === 0);
const userPlanName = computed(() => userInfo.value?.product_name || "Free");
const isDev = computed(() => store.state.user.is_dev);
const setIsDev = (isDev) => store.dispatch("user/setIsDev", isDev);

const username = computed(() => {
  const firstName = userInfo.value?.first_name || "";
  const lastName = userInfo.value?.last_name || "";
  return (
    `${firstName} ${lastName}`.trim() || userInfo.value?.email_address || ""
  );
});
const fullName = computed(() => {
  const firstName = userInfo.value?.first_name || "";
  const lastName = userInfo.value?.last_name || "";
  return `${firstName} ${lastName}`.trim();
});
const userEmail = computed(() => userInfo.value?.email_address || "");
const isLocal = computed(() =>
  ["production", "test1"].includes(__CUSTOM_ENV__) ? false : true
);

const isMobile = useMediaQuery("(max-width: 768px)");
const userMenuMode = computed(() => (isMobile.value ? "click" : "hover"));
const creditsPopoverMode = computed(() => (isMobile.value ? "click" : "hover"));
const userMenuOpen = ref(false);
const isLoggingOut = ref(false);
const mobileMenuOpen = ref(false);
const activeMobileSection = ref("");

const { data: videoEffectsRaw } = useAsyncData(
  "home-header-video-effects-index",
  () => r2CachedFetch("/api/r2/ai/effects/video/index.json"),
  { default: () => [] }
);
const { data: imageEffectsRaw } = useAsyncData(
  "home-header-image-effects-index",
  () => r2CachedFetch("/api/r2/ai/effects/image/index.json"),
  { default: () => [] }
);

const toolGroupConfig = {
  "Image AI": {
    icon: "material-symbols-light:imagesmode-outline-rounded",
    accent: "var(--sf-image)",
  },
  "Video AI": {
    icon: "material-symbols-light:video-camera-back-add-rounded",
    accent: "var(--sf-video)",
  },
  "Audio AI": {
    icon: "material-symbols-light:music-video-rounded",
    accent: "var(--sf-audio)",
  },
};

// Free Tools 下拉:按 Image / Video / Audio 三大分类。每类一个枢纽页(hub)+ 数个热门工具直链。
// 路由全部为已确认的 canonical:
//  - 枢纽:/free-tools/{image,video,audio}/  (app/pages/free-tools/{image,video,audio}/index.vue)
//  - Image 单工具走 SINGLE_TOOL_ROUTES(utils/ftHref):converter/compressor/resize-image/crop-image…
//  - Video/Audio 单工具 canonical = /free-tools/<seg>/<slugify(name)>/(seo-data generate());
//    三个转换器枢纽分别是 image=converter、video=video-converter、audio=audio-converter(seo-overrides 已确认)。
// 只放非配对(non-pair)单工具,避开 "X to Y" 配对页的 /image/converter/ 归类歧义。
const freeToolCategories = [
  {
    label: "Image",
    accent: "var(--sf-image)",
    to: "/free-tools/image/",
    allLabel: "All image tools",
    tools: [
      { label: "Image Converter", to: "/free-tools/image/converter/" },
      { label: "Image Compressor", to: "/free-tools/image/compressor/" },
      { label: "Resize Image", to: "/free-tools/image/resize-image/" },
      { label: "Crop Image", to: "/free-tools/image/crop-image/" },
      { label: "Rotate Image", to: "/free-tools/image/rotate-image/" },
      { label: "Flip Image", to: "/free-tools/image/flip-image/" },
    ],
  },
  {
    label: "Video",
    accent: "var(--sf-video)",
    to: "/free-tools/video/",
    allLabel: "All video tools",
    tools: [
      { label: "Video Converter", to: "/free-tools/video/video-converter/" },
      { label: "Video Compressor", to: "/free-tools/video/video-compressor/" },
      { label: "Trim Video", to: "/free-tools/video/trim-video/" },
      { label: "Crop Video", to: "/free-tools/video/crop-video/" },
      { label: "Mute Video", to: "/free-tools/video/mute-video/" },
    ],
  },
  {
    label: "Audio",
    accent: "var(--sf-audio)",
    to: "/free-tools/audio/",
    allLabel: "All audio tools",
    tools: [
      { label: "Audio Converter", to: "/free-tools/audio/audio-converter/" },
      { label: "Audio Trimmer", to: "/free-tools/audio/audio-trimmer/" },
      { label: "Merge Audio", to: "/free-tools/audio/merge-audio/" },
      { label: "Ringtone Maker", to: "/free-tools/audio/ringtone-maker/" },
      { label: "Voice Recorder", to: "/free-tools/audio/voice-recorder/" },
    ],
  },
];

function isExternalUrl(to) {
  return typeof to === "string" && /^https?:\/\//i.test(to);
}

const headerMenuGroups = computed(() =>
  menuGroupsForArea(menuData.value, "header")
);
const productIndexes = computed(() => createProductIndexes(productList.value));

const primaryToolGroups = computed(() =>
  Object.entries(toolGroupConfig).map(([label, config]) => {
    const group = findMenuGroup(headerMenuGroups.value, label);
    const children = (group?.children || []).map((item) => {
      const product = productForMenuItem(item, productIndexes.value);
      return {
        label: product?.title || item.title,
        description: product?.description || "",
        to: toInternalRoute(item.url),
        target: item?.target || undefined,
      };
    });
    return { label, ...config, children };
  })
);

const aiModelItems = computed(() =>
  enabledModels(modelList.value).map((model) => ({
    label: model.modelName || model.title,
    description: model.description,
    vendor: model.vendor,
    avatar: model?.avatar?.src || "",
    to: toInternalRoute(model.url),
    groups: Array.isArray(model.groups) ? model.groups : [],
    sortIndex: model.sortIndex,
  }))
);

const navModels = computed(() => aiModelItems.value.sort((a, b) => a.sortIndex - b.sortIndex).slice(0, 10));

function indexEffects(raw, type, limit) {
  return normalizeEffectsList(raw)
    .filter((effect) => effect?.enabled !== 0 && effect?.code)
    .slice(0, limit)
    .map((effect) => ({
      ...effect,
      title: effect.title || effect.code,
      to: `/ai/effects/${effect.effect_type || type}/${effect.code}/`,
    }));
}

const navVideoEffects = computed(() =>
  indexEffects(videoEffectsRaw.value, "video", 6)
);
const navImageEffects = computed(() =>
  indexEffects(imageEffectsRaw.value, "image", 6)
);

const mobileSections = computed(() => {
  const sections = primaryToolGroups.value.map((group) => ({
    label: group.label,
    accent: group.accent,
    children: group.children,
  }));

  sections.push({
    label: "AI Effects",
    accent: "var(--sf-brand)",
    children: [
      { type: "label", label: "Video Effects", key: "video-effects-label" },
      ...navVideoEffects.value.map((effect) => ({
        label: effect.title,
        to: effect.to,
      })),
      { type: "label", label: "Image Effects", key: "image-effects-label" },
      ...navImageEffects.value.map((effect) => ({
        label: effect.title,
        to: effect.to,
      })),
    ],
  });

  sections.push({
    label: "AI Models",
    accent: "var(--sf-models)",
    children: [
      ...navModels.value.map((model) => ({ label: model.label, to: model.to })),
      { label: "View all models →", to: "/ai/models/" },
    ],
  });

  freeToolCategories.forEach((cat) => {
    sections.push({
      label: "Free Tools · " + cat.label,
      accent: cat.accent,
      children: [
        ...cat.tools,
        { label: cat.allLabel + " →", to: cat.to },
      ],
    });
  });

  return sections;
});

function fmtCredits(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

function createSelectEvent() {
  return {
    defaultPrevented: false,
    preventDefault() {
      this.defaultPrevented = true;
    },
  };
}

function closeUserMenu(close) {
  userMenuOpen.value = false;
  close?.();
}

function onUserMenuItemSelect(item, close) {
  const e = createSelectEvent();
  item?.onSelect?.(e);
  if (item?.type === "checkbox") return;
  if (!e.defaultPrevented) closeUserMenu(close);
}

function toggleMobileSection(label) {
  activeMobileSection.value = activeMobileSection.value === label ? "" : label;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

function openSignInFromMobile() {
  closeMobileMenu();
  openSignInModal();
}

function openSignUpFromMobile() {
  closeMobileMenu();
  openSignUpModal();
}

const logout = async () => {
  isLoggingOut.value = true;
  try {
    await store.dispatch("user/logout", {
      userId: userInfo.value.user_info_id,
    });
    window.location.reload();
  } catch (error) {
    console.error("Logout failed:", error);
    isLoggingOut.value = false;
  }
};

const userMenuItems = computed(() => {
  const mainItems = [
    {
      label: "My Tasks",
      icon: "material-symbols-light:folder-check-rounded",
      to: "/my/tasks/",
      active: route.path.startsWith("/my/tasks/"),
    },
    {
      label: "Pricing",
      icon: "i-lucide-dollar-sign",
      to: "/pricing/",
      active: route.path.startsWith("/pricing/"),
    },
    {
      label: "Refer a Friend",
      icon: "i-lucide-party-popper",
      class: "text-yellow-400",
      to: "/my/earn-credits/",
      active: route.path.startsWith("/my/earn-credits/"),
    },
    {
      label: "My Plan & Credits",
      icon: "material-symbols-light:diamond-shine-outline-rounded",
      to: "/my/plan-credits/",
      active: route.path.startsWith("/my/plan-credits/"),
    },
    {
      label: "My Invoices",
      icon: "material-symbols-light:receipt-rounded",
      to: "/my/invoices/",
      active: route.path.startsWith("/my/invoices/"),
    },
    {
      label: "My Profile",
      icon: "i-lucide-user",
      to: "/my/profile/",
      active: route.path.startsWith("/my/profile/"),
    },
    {
      label: "Contact Us",
      icon: "i-lucide-mail",
      to: "https://sharkfoto.com/company/contact-us/",
    },
  ];

  const items = [
    [
      {
        label: username.value || "Account",
        avatar: { src: avatarUrl.value, alt: username.value, size: "md" },
        type: "label",
      },
    ],
    mainItems,
  ];

  if (isLocal.value) {
    items.push([
      {
        label: "调试",
        icon: "i-lucide-bug",
        type: "checkbox",
        checked: Boolean(isDev.value),
        onUpdateChecked(checked) {
          setIsDev(Boolean(checked));
        },
        onSelect(e) {
          e.preventDefault();
          setIsDev(!isDev.value);
        },
      },
    ]);
  }

  items.push([
    {
      label: "Log Out",
      icon: isLoggingOut.value ? "eos-icons:loading" : "i-lucide-log-out",
      color: "error",
      onSelect(e) {
        e.preventDefault();
        logout();
      },
    },
  ]);

  return items;
});
</script>

<style scoped>
.sf-nav {
  --sf-surface: #10131b;
  --sf-line: rgba(255, 255, 255, 0.09);
  --sf-line-strong: rgba(255, 255, 255, 0.16);
  --sf-tx: #f4f6fa;
  --sf-tx-dim: #ffffff;
  --sf-tx-faint: #ffffff;
  --sf-image: #ff6f9c;
  --sf-video: #4ea2ff;
  --sf-audio: #22d3ee;
  --sf-models: #34d399;
  --sf-free: #fbbf24;
  --sf-brand: #8b5cf6;
  --sf-brand-2: #b06bfb;
  position: relative;
  z-index: 80;
  border-bottom: 1px solid var(--sf-line);
  color: var(--sf-tx);
  font-family: "Public Sans", system-ui, sans-serif;
  backdrop-filter: blur(18px);
}

.sf-nav-inner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  min-width: 0;
  height: 66px;
  padding: 0 16px;
}

.sf-logo {
  justify-self: start;
  color: #fff;
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.sf-logo b {
  color: var(--sf-brand);
  font-weight: 700;
}

.sf-menu {
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  gap: 1px;
  min-width: 0;
}

.sf-menu-item {
  position: relative;
}

.sf-menu-link {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 11px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--sf-tx-dim);
  font-size: 14.5px;
  /* font-weight: 600; */
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.18s;
}

.sf-menu-link:hover,
.sf-menu-item:hover .sf-menu-link {
  background: rgba(255, 255, 255, 0.05);
  color: var(--sf-tx);
}

.sf-mi-ico {
  width: 17px;
  height: 17px;
  color: var(--sf-tx-faint);
  transition: 0.18s;
}

.sf-menu-link:hover .sf-mi-ico,
.sf-menu-item:hover .sf-mi-ico {
  color: var(--sf-brand);
}

.sf-chev {
  width: 13px;
  height: 13px;
  opacity: 0.55;
  transition: 0.2s;
}

.sf-menu-item:hover .sf-chev {
  transform: rotate(180deg);
}

.sf-mega {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  min-width: 260px;
  padding: 16px;
  border: 1px solid var(--sf-line-strong);
  border-radius: 18px;
  background: var(--sf-surface);
  box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.8);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(-50%) translateY(8px);
  transition: 0.22s cubic-bezier(0.2, 0.7, 0.3, 1);
}

.sf-mega::before {
  content: "";
  position: absolute;
  top: -12px;
  right: 0;
  left: 0;
  height: 12px;
}

.sf-menu-item:hover .sf-mega {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

.sf-effects-mega {
  width: min(560px, 92vw);
}

.sf-models-mega {
  right: -30px;
  left: auto;
  width: min(640px, 92vw);
  transform: translateY(8px);
}

.sf-menu-item:hover .sf-models-mega {
  transform: translateY(0);
}

.sf-mega-grid {
  display: grid;
  gap: 4px;
}

.sf-mega-grid-2 {
  grid-template-columns: 1fr 1fr;
}

/* Free Tools mega: 三列分类(Image / Video / Audio) */
.sf-free-mega {
  width: min(620px, 92vw);
}

.sf-free-cols {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.sf-free-col-head {
  padding: 8px 12px 4px;
  color: var(--accent);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.sf-free-all {
  color: var(--accent);
  font-size: 13px;
}

@media (max-width: 640px) {
  .sf-free-cols {
    grid-template-columns: 1fr;
  }
}

.sf-mega-head {
  padding: 6px 12px 8px;
  color: var(--sf-tx-faint);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.sf-mega-link {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 12px;
  border-radius: 11px;
  color: var(--sf-tx-dim);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  transition: 0.15s;
}

.sf-mega-link:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--sf-tx);
}

.sf-dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
}

.sf-video-dot {
  background: var(--sf-video);
}

.sf-image-dot {
  background: var(--sf-image);
}

.sf-video-text {
  color: var(--sf-video);
}

.sf-image-text {
  color: var(--sf-image);
}

.sf-view-all {
  font-weight: 700;
}

.sf-mm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
}

.sf-mm-link {
  display: flex;
  gap: 11px;
  padding: 10px 11px;
  border-radius: 12px;
  transition: 0.15s;
}

.sf-mm-link:hover {
  background: rgba(255, 255, 255, 0.06);
}

.sf-mm-ico {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  overflow: hidden;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.sf-mm-ico img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.sf-mm-txt {
  min-width: 0;
}

.sf-mm-name,
.sf-mm-desc {
  display: block;
}

.sf-mm-name {
  color: var(--sf-tx);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.sf-mm-desc {
  display: -webkit-box;
  margin-top: 2px;
  overflow: hidden;
  color: var(--sf-tx-faint);
  font-size: 11.5px;
  line-height: 1.3;
  -webkit-box-orient: vertical;
  line-clamp: 2;
}

.sf-mm-foot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 6px;
  padding: 11px;
  border-radius: 11px;
  background: rgba(52, 211, 153, 0.13);
  color: var(--sf-models);
  font-size: 13.5px;
  font-weight: 700;
  transition: 0.15s;
}

.sf-mm-foot:hover {
  background: rgba(52, 211, 153, 0.22);
}

.sf-nav-cta {
  display: flex;
  align-items: center;
  justify-self: end;
  gap: 8px;
}

.sf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 7px 14px;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.18s;
}

.sf-btn-primary {
  background: linear-gradient(135deg, #7c3aed, #9d5cf7);
  color: #fff;
  box-shadow: 0 8px 22px -10px rgba(124, 58, 237, 0.8);
}

.sf-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -10px rgba(124, 58, 237, 0.9);
}

.sf-btn-outline {
  border-color: rgba(255, 255, 255, 0.22);
  background: transparent;
  color: var(--sf-tx);
}

.sf-btn-outline:hover {
  border-color: var(--sf-brand);
  background: rgba(139, 92, 246, 0.14);
  color: #fff;
}

.sf-credits-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.sf-avatar-btn {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.sf-user-menu-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: 0.15s;
}

.sf-user-menu-row:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.sf-mobtoggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid var(--sf-line-strong);
  border-radius: 10px;
  background: transparent;
  color: var(--sf-tx);
  cursor: pointer;
}

.sf-mobile-menu {
  display: none;
  flex-direction: column;
  max-height: calc(100vh - 66px);
  overflow-y: auto;
  padding: 8px 40px 24px;
  border-top: 1px solid var(--sf-line);
  background: rgba(10, 12, 18, 0.98);
}

.sf-mobile-menu.open {
  display: flex;
}

.sf-mob-acc {
  border-bottom: 1px solid var(--sf-line);
}

.sf-mob-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 15px 4px;
  border: 0;
  background: transparent;
  color: var(--sf-tx);
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.sf-mob-title {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  white-space: nowrap;
}

.sf-mob-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.sf-mob-chev {
  width: 18px;
  height: 18px;
  color: var(--sf-tx-faint);
  transition: 0.22s;
}

.sf-mob-acc.open .sf-mob-chev {
  color: var(--sf-brand);
  transform: rotate(180deg);
}

.sf-mob-sub {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.26s ease;
}

.sf-mob-acc.open .sf-mob-sub {
  grid-template-rows: 1fr;
}

.sf-mob-sub-in {
  min-height: 0;
  overflow: hidden;
}

.sf-mob-sub-pad {
  padding-bottom: 10px;
}

.sf-mob-sublabel {
  padding: 13px 4px 5px 19px;
  color: var(--sf-tx-faint);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.sf-mob-link-child {
  display: block;
  padding: 11px 4px 11px 19px;
  color: var(--sf-tx-dim);
  font-size: 14.5px;
  font-weight: 500;
  transition: 0.15s;
}

.sf-mob-link-child:hover {
  color: var(--sf-tx);
}

.sf-mob-link {
  display: block;
  padding: 15px 4px;
  border-bottom: 1px solid var(--sf-line);
  color: var(--sf-tx);
  font-size: 16px;
  font-weight: 600;
  transition: 0.15s;
}

.sf-mob-link:hover {
  color: var(--sf-brand);
}

.sf-mob-cta {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.sf-mob-cta .sf-btn {
  flex: 1;
  padding: 13px;
  font-size: 15px;
}

@media (max-width: 1180px) {
  .sf-menu {
    display: none;
  }

  .sf-mobtoggle {
    display: flex;
  }
}

@media (max-width: 640px) {
  .sf-desktop-auth {
    display: none;
  }

  .sf-nav-inner {
    padding: 0 18px;
  }

  .sf-mobile-menu {
    padding: 8px 18px 24px;
  }
}
</style>
